import { Alert, TextField } from '@carsys/ui'
import { DatePicker } from '@carsys/ui/src'
import DateType from '@carsys/ui/src/date-helpers/date-type'
import { notifier } from '@cbgms/base/notifier/use-notifier'
import { Box, FormControlLabel, IconButton, Switch } from '@mui/material'
import { makeStyles } from '@mui/styles'
import moment, { Moment } from 'moment'
import { ChangeEvent, MutableRefObject, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Header, Modal } from 'semantic-ui-react'
import { IAddArticleOffer, Offer } from './SalesGroupsList'
import * as types from './state/types'

export interface IAddEditOfferProps {
  open: boolean
  onCancel: () => void
  onConfirm: (payload: IAddArticleOffer, isEditing?: boolean) => Promise<void>
  isLoading: boolean
  isEditing?: boolean
  editingOffer?: Offer | null
}

enum AllowedFileTypes {
  CSV = 'text/csv'
}

const useStyles = makeStyles({
  boxDisplay: {
    display: 'flex',
    width: '100%'
  },
  marginTop: {
    marginTop: '8px'
  }
})

const sxBoxStyle = {
  display: 'flex',
  gap: 1,
  flexWrap: 'wrap',
  marginTop: '16px',
  '&>*': { flex: '1 1 auto' }
}
const sxDateStyle = { width: 'auto', minWidth: '200px' }

export const AddEditOffer = (props: IAddEditOfferProps) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [description, setDescription] = useState('')
  const [validForConcepts, setValidForConcepts] = useState('')
  const [mixingFromPosition, setMixingFromPosition] = useState('1')
  const [mixFile, setMixFile] = useState(true)
  const [validFrom, setValidFrom] = useState<Moment | null>(moment())
  const [validTo, setValidTo] = useState<Moment | null>(moment())
  const [descError, setDescError] = useState(false)
  const [mixingFromPositionError, setMixingFromPositionError] = useState(false)
  const [validFromError, setValidFromError] = useState(false)
  const [validToError, setValidToError] = useState(false)
  const [descHelperText, setDescHelperText] = useState('')
  const [mixFromPositionHText, setMixFromPositionHText] = useState('')
  const [validToHelperText, setValidToHelperText] = useState('')

  const [file, setFile] = useState<File | null>(null)
  const [fileName, setFileName] = useState('')
  const [fileError, setFileError] = useState(false)
  const [fileHelperText, setFHText] = useState('')

  const handleIconClick = () => {
    ;(fileInputRef as MutableRefObject<HTMLInputElement>).current.click()
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files as FileList
    const allowedTypes = Object.values(AllowedFileTypes)
    if (files.length > 0) {
      const fileType = files[0].type as AllowedFileTypes
      if (!allowedTypes.includes(fileType)) {
        event.target.value = ''
        setFileErrorFn()
        return notifier.error(t('articleOffer.add_attch_file_allowed'))
      }

      if (files[0].size > 1000000) {
        event.target.value = ''
        setFileErrorFn()
        return notifier.error(t('articleOffer.add_attch_info'))
      }
      setFile(files[0])
      setFileName(files[0].name)
      setFileError(false)
      setFHText('')
    } else {
      setFileErrorFn()
    }
  }

  const handleReuploadSwitchChange = () => {
    if (!mixFile) {
      setMixFile(true)
    } else {
      setMixFile(false)
    }
  }

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget
    if (name === types.nameType['OfferDescription']) {
      if (value === '') {
        setDescError(true)
        setDescHelperText(t('articleOffer.add_desc_error'))
      } else {
        setDescError(false)
        setDescHelperText('')
      }

      setDescription(value)
    }
    if (name === types.nameType['ValidForConcepts']) {
      setValidForConcepts(value)
    }
    if (name === types.nameType['MixingFromPosition']) {
      if (value === '') {
        setMixingFromPositionError(true)
        setMixFromPositionHText(t('articleOffer.add_mixingFromPosition_error'))
      } else {
        setMixingFromPositionError(false)
        setMixFromPositionHText('')
      }

      setMixingFromPosition(value)
    }
  }

  const setFileErrorFn = () => {
    setFile(null)
    setFileName('')
    setFileError(true)
    setFHText(t('articleOffer.add_attch_file_error'))
  }

  const reset = () => {
    setDescription('')
    setValidForConcepts('')
    setValidFrom(moment())
    setValidTo(moment())
    setMixFile(true)
    setMixingFromPosition('1')
    setDescError(false)
    setMixingFromPositionError(false)
    setDescHelperText('')
    setMixFromPositionHText('')
    setValidToHelperText('')
    setValidFromError(false)
    setValidToError(false)
    setFile(null)
    setFileName('')
    setFileError(false)
    setFHText('')
  }

  const handleOnCancel = () => {
    reset()
    props.onCancel()
  }

  const handleOnConfirm = async () => {
    if (validFrom && validTo && validFrom > validTo) {
      setValidToError(true)
      setValidToHelperText(t('articleOffer.add_validTo_dateerror'))
    } else {
      const payload = {
        ArticleOfferUUId: !props.isEditing ? '' : props.editingOffer?.ArticleOfferUUID || '',
        OfferDescription: description,
        ValidForConcepts: removeTrailingSemicolon(validForConcepts),
        ValidFrom: moment(validFrom).format('DD.MM.YYYY'),
        ValidTo: moment(validTo).format('DD.MM.YYYY'),
        MixFile: mixFile,
        MixingFromPosition: mixFile ? mixingFromPosition.toString() : props.isEditing ? props.editingOffer?.MixFrom.toString() || '1' : '1',
        File: file as File
      }
      if (!props.isEditing) await props.onConfirm(payload)
      else await props.onConfirm(payload, true)
      reset()
    }
  }
  const removeTrailingSemicolon = (validForConcpt: string) => {
    if (validForConcpt.endsWith(';')) {
      return validForConcpt.slice(0, -1)
    }
    return validForConcpt
  }

  useEffect(() => {
    if (props.isEditing && props.editingOffer) {
      setDescription(props.editingOffer.OfferDescription)
      setValidForConcepts(props.editingOffer.Concepts)
      setValidFrom(moment(props.editingOffer.ValidFrom, 'DD.MM.YYYY'))
      setValidTo(moment(props.editingOffer.ValidTo, 'DD.MM.YYYY'))
      setMixFile(props.editingOffer.MixUp)
      setMixingFromPosition(props.editingOffer.MixFrom.toString())
    }
  }, [props.isEditing, props.editingOffer])

  return (
    <Modal open={props.open} size='small' data-testid='add-edit-offer-modal'>
      <Header content={props.isEditing ? t('articleOffer.editOffer') : t('articleOffer.addOffer')} />
      <Modal.Content>
        <form noValidate>
          <TextField
            name={types.nameType['OfferDescription']}
            value={description}
            label={t('articleOffer.offerDesription')}
            onChange={(event: ChangeEvent<HTMLInputElement>) => handleOnChange(event)}
            helperText={descHelperText}
            error={descError}
            required
            margin='normal'
          />
          <TextField
            name={types.nameType['ValidForConcepts']}
            value={validForConcepts}
            label={t('articleOffer.validForConcepts')}
            onChange={(event: ChangeEvent<HTMLInputElement>) => handleOnChange(event)}
            margin='normal'
          />
          <Box sx={sxBoxStyle}>
            <DatePicker
              sx={sxDateStyle}
              label={t('articleOffer.validForm')}
              value={validFrom}
              minDate={moment()}
              onChange={(value: DateType) => {
                setValidFrom(value)
                setValidFromError(value === null)
              }}
              slotProps={{
                textField: {
                  required: true,
                  helperText: validFromError ? t('articleOffer.add_validFrom_error') : '',
                  error: validFromError
                }
              }}
            />
            <DatePicker
              sx={sxDateStyle}
              label={t('articleOffer.validUntil')}
              value={validTo}
              minDate={moment()}
              onChange={(value: DateType) => {
                setValidTo(value)
                setValidToError(value === null)
                setValidToHelperText(value === null ? t('articleOffer.add_validTo_error') : '')
              }}
              slotProps={{ textField: { required: true, helperText: validToError ? validToHelperText : '', error: validToError } }}
            />
          </Box>
          <FormControlLabel
            value='start'
            control={<Switch color='primary' checked={mixFile} onChange={handleReuploadSwitchChange} />}
            label={t('articleOffer.mix')}
            labelPlacement='start'
            className={classes.marginTop}
          />
          {mixFile && (
            <TextField
              name={types.nameType['MixingFromPosition']}
              value={mixingFromPosition}
              label={t('articleOffer.mixingFromPosition')}
              onChange={(event: ChangeEvent<HTMLInputElement>) => handleOnChange(event)}
              helperText={mixFromPositionHText}
              error={mixingFromPositionError}
              required
              margin='normal'
            />
          )}
          <Box className={classes.boxDisplay}>
            <TextField
              name={types.nameType['File']}
              value={fileName}
              label={t('email_attachment.add_attch_file')}
              helperText={fileHelperText}
              error={fileError}
              required={!props.isEditing}
              margin='normal'
              fullWidth
            />

            <IconButton color='primary' size='small' onClick={handleIconClick} data-testid='btn-upload-icon'>
              <i className='upload icon' />
            </IconButton>
          </Box>
          <input
            type={types.nameType['File']}
            multiple={false}
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
            accept={Object.values(AllowedFileTypes).join(',')}
            data-testid='btn-file-upload'
          />
        </form>

        <Alert severity='info'>{t('articleOffer.add_attch_info')}</Alert>
      </Modal.Content>
      <Modal.Actions>
        <Button content={t('global.cancel')} icon='cancel' onClick={handleOnCancel} data-testid='btn-cancel' />
        <Button
          content={t('global.save')}
          color='blue'
          onClick={handleOnConfirm}
          data-testid='btn-confirm'
          disabled={
            description.length === 0 ||
            validFrom === null ||
            validTo === null ||
            (mixFile && mixingFromPosition === '') ||
            (!props.isEditing && fileName.length === 0)
          }
          loading={props.isLoading}
        />
      </Modal.Actions>
    </Modal>
  )
}
export default AddEditOffer
